import React, { useEffect, useContext } from 'react';
import track from 'react-tracking';
import PropTypes from 'prop-types';
import { Icon } from '@edx/paragon';
import {
  Facebook, BsTwitter, BsLinkedin, Email,
} from '@edx/paragon/icons';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  EmailShareButton,
} from 'react-share';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'gatsby-plugin-react-intl';
import { WebviewContext } from '@utils/context';
import { submitEvent } from '@utils/analytics';
import { getUserLoggedIn } from '@data/actions/user';
import { getFullMarketingUrl } from '@utils/course';

import { intlShape } from '@prospectus/common/types/intlShape';

const messages = defineMessages({
  'prospectus.course.detail.socialLinks.header': {
    id: 'prospectus.course.detail.socialLinks.header',
    defaultMessage: 'Share this course',
    description: 'Header for social media links on a course detail page',
  },
  'prospectus.course.detail.socialLinks.srText.facebook': {
    id: 'prospectus.course.detail.socialLinks.srText.facebook',
    defaultMessage: 'Share this course on facebook',
    description: 'Screenreader text for the edX Facebook social media link.',
  },
  'prospectus.course.detail.socialLinks.srText.twitter': {
    id: 'prospectus.course.detail.socialLinks.srText.twitter',
    defaultMessage: 'Share this course on twitter',
    description: 'Screenreader text for the edX Twitter social media link.',
  },
  'prospectus.course.detail.socialLinks.srText.linkedin': {
    id: 'prospectus.course.detail.socialLinks.srText.linkedin',
    defaultMessage: 'Share this course on LinkedIn',
    description: 'Screenreader text for the edX LinkedIn social media link.',
  },
  'prospectus.course.detail.socialLinks.srText.email': {
    id: 'prospectus.course.detail.socialLinks.srText.email',
    defaultMessage: 'Share this course via email',
    description: 'Screenreader text for the edX email social media link.',
  },
  'prospectus.course.detail.socialLinks.share': {
    id: 'prospectus.course.detail.socialLinks.share',
    description: 'language prompting user to share the page on a particular social media platform',
    defaultMessage: 'Share this page on {site}!',
  },
});

function SocialShare({
  title,
  prospectusPath,
  activeCourseRun: {
    marketingUrl,
    key,
  },
  intl,
  tracking,
  requestUserLoggedIn,
  userLoggedIn,
  mobile,
}) {
  useEffect(() => {
    requestUserLoggedIn();
  }, []);

  const getShareLinkWithUtmParams = (source) => {
    const shareUrl = getFullMarketingUrl({ prospectusPath }, intl.locale) || marketingUrl;
    if (!source || source === '') {
      return shareUrl;
    }

    const campaign = 'social-sharing-course-page';
    const medium = 'social';
    return `${shareUrl}?utm_campaign=${campaign}&utm_medium=${medium}&utm_source=${source}`;
  };

  const [FACEBOOK_SOURCE, TWITTER_SOURCE, LINKEDIN_SOURCE, EMAIL_SOURCE] = ['facebook', 'twitter', 'linkedin', 'email'];

  const socialLinks = [
    {
      icon: <Icon src={Facebook} className="text-white" />,
      screenReaderText: intl.formatMessage(messages['prospectus.course.detail.socialLinks.srText.facebook']),
      component: FacebookShareButton,
      source: FACEBOOK_SOURCE,
      event: 'edx.bi.course.socialshare.facebook',
    },
    {
      icon: <Icon src={BsTwitter} className="text-white" />,
      screenReaderText: intl.formatMessage(messages['prospectus.course.detail.socialLinks.srText.twitter']),
      component: TwitterShareButton,
      source: TWITTER_SOURCE,
      event: 'edx.bi.course.socialshare.twitter',
    },
    {
      icon: <Icon src={BsLinkedin} className="text-white" size="lg" />,
      screenReaderText: intl.formatMessage(messages['prospectus.course.detail.socialLinks.srText.linkedin']),
      component: LinkedinShareButton,
      source: LINKEDIN_SOURCE,
      event: 'edx.bi.course.socialshare.linkedin',
    },
    {
      icon: <Email className="text-white" size="lg" />,
      screenReaderText: intl.formatMessage(messages['prospectus.course.detail.socialLinks.srText.email']),
      component: EmailShareButton,
      source: EMAIL_SOURCE,
      event: 'edx.bi.course.socialshare.email',
    },
  ];

  const webview = useContext(WebviewContext);

  // this is to solve a bug where the share buttons were not working on iOS
  // navigator.share opens a menu that allows the user to share this link via their available apps
  // navigator.share is supported on iOS webview but not android webview
  const openShareMenu = async () => {
    const shareData = {
      title,
      text: title,
      url: getShareLinkWithUtmParams('iOS-share-menu'),
    };

    try {
      await navigator.share(shareData);
    } catch (err) {
      // AbortError is the error that is thrown when user cancels the share
      if (err.name !== 'AbortError') {
        throw err;
      }
    }
  };

  const handleOnClick = (event) => {
    submitEvent(tracking, { user_logged_in: userLoggedIn }, event);
    openShareMenu();
  };

  const getSocialLinkHTML = (socialLink) => {
    const {
      source, screenReaderText, icon, component: ShareComponent, event,
    } = socialLink;

    const isiOSWebview = webview && navigator.share;

    if (isiOSWebview) {
      return (
        <li key={`${source}-${key}`} className="mx-2">
          <button
            type="button"
            className="social-media-share-button text-white d-flex justify-content-center align-items-center rounded-circle bg-primary-500"
            onClick={() => handleOnClick(socialLink.event)}
          >
            {icon}
            <span className="sr-only">{screenReaderText}</span>
          </button>
        </li>
      );
    }

    const shareComponentProps = {
      url: getShareLinkWithUtmParams(source),
      beforeOnClick: () => submitEvent(tracking, { user_logged_in: userLoggedIn }, event),
    };

    if (source === EMAIL_SOURCE) {
      shareComponentProps.subject = title;
    } else if (source === TWITTER_SOURCE) {
      shareComponentProps.title = title;
    }

    return (
      <li key={`${source}-${key}`} className="mx-2">
        <ShareComponent {...shareComponentProps}>
          <div
            className="social-media-share-button text-white d-flex justify-content-center align-items-center rounded-circle bg-primary-500"
          >
            {icon}
            <span className="sr-only">{screenReaderText}</span>
          </div>
        </ShareComponent>
      </li>
    );
  };

  return (
    <div className={`${mobile ? '' : 'bg-light-500 py-4'}`}>
      {!mobile && (
        <h1 className="text-center pt-2 pb-3 py-sm-3 h2-sm">
          {intl.formatMessage(messages['prospectus.course.detail.socialLinks.header'])}
        </h1>
      )}
      <div className="social-links">
        <ul className={`medium-column d-flex flex-row justify-content-center list-unstyled p-0 ${mobile ? 'mb-0' : 'mb-2 mb-sm-3'}`}>
          {socialLinks.map((socialLink) => getSocialLinkHTML(socialLink))}
        </ul>
      </div>
    </div>
  );
}

SocialShare.propTypes = {
  title: PropTypes.string.isRequired,
  prospectusPath: PropTypes.string,
  activeCourseRun: PropTypes.shape({
    marketingUrl: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  userLoggedIn: PropTypes.bool,
  requestUserLoggedIn: PropTypes.func,
  intl: intlShape.isRequired,
  tracking: PropTypes.shape().isRequired,
  mobile: PropTypes.bool,
};

SocialShare.defaultProps = {
  prospectusPath: '',
  userLoggedIn: false,
  requestUserLoggedIn: () => {
  },
  mobile: false,
};

const mapStateToProps = store => ({
  userLoggedIn: store.userLoggedIn,
});

const mapDispatchToProps = dispatch => ({
  requestUserLoggedIn() {
    dispatch(getUserLoggedIn());
  },
});

const ConnectedSocialShare = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(SocialShare));

export default track({
  component: 'social-share',
})(injectIntl(ConnectedSocialShare));
